import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, getAuthToken, setAuthToken } from '@/lib/api';
import { pushSessionTokenToExtension } from '@/lib/extensionBridge';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      // Best-effort: sync token to the extension so Guided Scans work
      // seamlessly. No-op if the extension isn't installed.
      pushSessionTokenToExtension(token);
    } catch {
      setAuthToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, remember_me = false) => {
    const { data } = await api.post('/auth/login', { email, password, remember_me });
    setAuthToken(data.token);
    setUser(data.user);
    pushSessionTokenToExtension(data.token);
    return data.user;
  };

  // Owner model v2.1 — single-shot login when the user isn't signed in yet.
  // Backend endpoint accepts { email, password, pin }.
  const adminLogin = async (email, password, pin) => {
    const { data } = await api.post('/admin/login', { email, password, pin });
    setAuthToken(data.token);
    setUser(data.user);
    pushSessionTokenToExtension(data.token);
    return data.user;
  };

  // Owner model v2.1 — PIN step-up for an already-signed-in Super Admin.
  // Backend endpoint accepts { pin } and returns a fresh JWT with adm=true.
  const adminStepUp = async (pin) => {
    const { data } = await api.post('/admin/step-up', { pin });
    setAuthToken(data.token);
    setUser(data.user);
    pushSessionTokenToExtension(data.token);
    return data.user;
  };

  const register = async (email, password, honeypot = {}) => {
    const { data } = await api.post('/auth/register', {
      email,
      password,
      ...honeypot,
    });
    setAuthToken(data.token);
    setUser(data.user);
    pushSessionTokenToExtension(data.token);
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setAuthToken(null);
    setUser(null);
    pushSessionTokenToExtension(null);
  };

  const value = { user, loading, login, adminLogin, adminStepUp, register, logout, refresh, setUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
